import { useFormatDate } from "@/hooks/date";
import usePopoverState from "@/hooks/usePopoverState";
import { cn } from "@/lib/cn";
import { defaultNS, useTranslation } from "@/lib/i18n";
import { formatISODate } from "@/utils/date";
import { forwardRef, useCallback } from "react";
import Icon from "../Icon/Icon";
import { HStack } from "../Layout";
import { Button } from "../ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Calendar } from "./Calendar";

type OnChange = (value: Date | undefined) => void;

type Props = {
  id?: string;
  disabled?: boolean;
  name?: string;
  className?: string;
  value: Date | undefined;
  onChange?: OnChange;
  placeholder?: string;
  width?: number | string;
  disablePast?: boolean;
  disableToday?: boolean;
  showYearMonthSelect?: boolean;
  allowEmpty?: boolean;
};

export const SimpleDatePicker = forwardRef<HTMLButtonElement, Props>(
  function SimpleDatePicker(
    {
      id,
      disabled,
      name,
      className,
      value,
      onChange,
      placeholder,
      width,
      disablePast,
      disableToday,
      showYearMonthSelect = true,
      allowEmpty = false,
    }: Props,
    ref,
  ) {
    const { t } = useTranslation(defaultNS, {
      keyPrefix: "component.DatePicker",
    });
    const [open, setOpen] = usePopoverState();
    const formatDate = useFormatDate();

    const handleSelect = useCallback<OnChange>(
      (date) => setOpen(false).then(() => onChange?.(date)),
      [onChange, setOpen],
    );

    // 今日の日付を0時0分0秒にセット
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // 明日の日付を0時0分0秒にセット
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    return (
      <Popover open={open} onOpenChange={setOpen}>
        <HStack>
          <PopoverTrigger asChild>
            <Button
              ref={ref}
              id={id}
              disabled={disabled}
              name={name}
              type="button"
              variant="outline"
              value={value ? formatISODate(value) : ""}
              className={cn(
                "flex w-full justify-between bg-transparent px-3",
                className,
                typeof width === "number" ? `w-[${width}px]` : width,
              )}
            >
              <Icon iconName="CalendarIcon" className="opacity-50 shrink-0" />
              {value ? (
                <span className="flex-1 ml-2 text-left">
                  {formatDate(value)}
                </span>
              ) : (
                <span className="flex-1 ml-2 text-left text-muted-foreground">
                  {placeholder ?? t("placeholder")}
                </span>
              )}
            </Button>
          </PopoverTrigger>
          {allowEmpty && value && !disabled && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => onChange?.(undefined)}
            >
              <Icon iconName="XIcon" />
            </Button>
          )}
        </HStack>
        <PopoverContent className="w-[252px] p-0">
          <Calendar
            mode="single"
            selected={value}
            onSelect={handleSelect}
            defaultMonth={value}
            showYearMonthSelect={showYearMonthSelect}
            fromDate={disableToday ? tomorrow : disablePast ? today : undefined}
          />
        </PopoverContent>
      </Popover>
    );
  },
);
